// Estimator Leads: every home that got a heat pump estimate, every solar
// callback and every contact form enquiry. Each row shows the address, what was
// shown and its type (callback, instant quote, estimate only, contact form);
// opening a row shows everything we hold: when, which component, the
// homeowner's answers (against the EPC) and the full calculation.
//
// Addresses only here: names, emails and phone numbers are emailed to the
// installer and not kept with the lead (an instant quote keeps the customer's
// details on the quote itself, in Quotes). Records are deleted a year after the
// home last used the tool.
import { Fragment, useEffect, useMemo, useState } from "react";
import { Calculator, ChevronRight, FilePlus2, FileText, Mail, PhoneCall, Search } from "lucide-react";
import { PRODUCT_LABELS, ProductIcon, type Product } from "../components/ProductIcon";
import { Link } from "react-router-dom";
import { estimateRequiredKw, quoteWorking, type PropertyDetails } from "@widget";
import { useApi, type EnquiryRecord, type HomeDetail, type HomeRecord, type SolarLeadSystem, type StatsRange, type Widget } from "../api";
import { RANGES, fmt, rangeLabel } from "../components/analytics";

const PROPERTY: Record<string, string> = { detached: "Detached", "semi-detached": "Semi-detached", terraced: "Terraced", bungalow: "Bungalow", flat: "Flat" };
const HEATING: Record<string, string> = { gas: "Gas boiler", oil: "Oil", lpg: "LPG", electric: "Electric", other: "Other" };
const gbp = (n: number) => `£${n.toLocaleString("en-GB")}`;
const when = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

/** What a lead is: the badge in the Type column. */
type LeadType = "estimate" | "callback" | "quote" | "contact";
const TYPE_LABELS: Record<LeadType, string> = { estimate: "Estimate", callback: "Callback", quote: "Instant quote", contact: "Contact form" };

/** A solar system in one line: "£6,450 – £7,900 · 10 panels (4.35 kWp)". */
const solarLine = (s: SolarLeadSystem) => `${gbp(s.low)} – ${gbp(s.high)} · ${s.panels} panels (${s.kwp.toFixed(2)} kWp)${s.battery ? " + battery" : ""}`;

/** One table row, with every field as display text (what the search matches on). */
interface Row {
  key: string;
  type: LeadType;
  product: Product;
  record: HomeRecord | null; // a home that got an estimate
  enquiry: EnquiryRecord | null; // a contact form enquiry or a solar callback
  address: string;
  quoted: string; // the Details column
  status: string;
  searchText: string;
  sortAt: string;
}

/** A contact form enquiry, or a solar callback, as a row. */
function toEnquiryRow(e: EnquiryRecord, names: Map<string, string>): Row {
  const solar = e.kind === "solar";
  const address = e.line ?? (solar ? "Address not recorded" : "No address asked");
  // Solar: the system and price range shown; no system = the roof couldn't be scanned
  const quoted = solar ? (e.system ? solarLine(e.system) : "No price: roof not scanned") : (e.topic ?? "Enquiry");
  const type: LeadType = solar ? "callback" : "contact";
  const product: Product = solar ? "solar" : "contact";
  return {
    key: e.id,
    type,
    product,
    record: null,
    enquiry: e,
    address,
    quoted,
    status: TYPE_LABELS[type],
    searchText: [address, quoted, TYPE_LABELS[type], PRODUCT_LABELS[product], names.get(e.widgetId) ?? "", when(e.createdAt)].join(" ").toLowerCase(),
    sortAt: e.createdAt,
  };
}

function toRow(a: HomeRecord, names: Map<string, string>): Row {
  const d = a.details;
  const address = a.line ?? "Address not recorded";
  const quoted = !a.quote
    ? "—"
    : a.quote.noEstimate
      ? "No price: survey needed"
      : a.quote.tooBig
      ? `No price: needs more than ${a.quote.heatPumpKw} kW`
      : `${gbp(a.quote.low)} – ${gbp(a.quote.high)} · ${a.quote.heatPumpKw} kW`;
  const type: LeadType = a.callbackDelivery?.method === "quote" ? "quote" : a.callbackRequested ? "callback" : "estimate";
  const status = TYPE_LABELS[type];
  const home = d ? `${PROPERTY[d.propertyType] ?? d.propertyType} ${d.bedrooms} bed ${HEATING[d.heating] ?? d.heating}` : "";
  return {
    key: `${a.widgetId}-${a.uprn}`,
    type,
    product: "ashp", // home records come from heat pump estimates only
    record: a,
    enquiry: null,
    address,
    quoted,
    status,
    // The search box matches everything in the row and its details
    searchText: [address, a.uprn, quoted, status, home, PRODUCT_LABELS.ashp, names.get(a.widgetId) ?? "", when(a.quotedAt ?? a.lastSeen)].join(" ").toLowerCase(),
    sortAt: a.quotedAt ?? a.lastSeen,
  };
}

export function LeadsPage() {
  const api = useApi();
  const [range, setRange] = useState<StatsRange>("90d");
  const [search, setSearch] = useState("");
  const [widgets, setWidgets] = useState<Widget[]>([]);
  const [homes, setHomes] = useState<HomeRecord[] | null>(null);
  const [enquiries, setEnquiries] = useState<EnquiryRecord[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null); // key of the expanded row

  useEffect(() => {
    api.listWidgets().then(setWidgets).catch(() => undefined);
  }, [api]);

  // Reload when the period changes (all widgets: most installers have just one)
  useEffect(() => {
    let live = true;
    setError(null);
    api
      .getAddresses(range)
      .then((d) => {
        if (!live) return;
        setHomes(d.addresses);
        setEnquiries(d.enquiries ?? []);
      })
      .catch((e: Error) => live && setError(e.message));
    return () => {
      live = false;
    };
  }, [api, range]);

  const names = useMemo(() => new Map(widgets.map((w) => [w.widgetId, w.name])), [widgets]);

  // Homes that got an estimate and contact form enquiries, newest first
  const rows = useMemo(
    () =>
      [...(homes ?? []).filter((h) => h.quote).map((h) => toRow(h, names)), ...enquiries.map((e) => toEnquiryRow(e, names))].sort((a, b) =>
        b.sortAt.localeCompare(a.sortAt),
      ),
    [homes, enquiries, names],
  );

  // Every word typed must appear somewhere in the row
  const shown = useMemo(() => {
    const words = search.toLowerCase().split(/\s+/).filter(Boolean);
    return words.length ? rows.filter((r) => words.every((w) => r.searchText.includes(w))) : rows;
  }, [rows, search]);

  // The summary counts each row once, by its Type badge (an instant quote isn't
  // also a callback or an estimate), listing only the types there are
  const counted: [LeadType, string, string][] = [
    ["callback", "callback", "callbacks"],
    ["quote", "instant quote", "instant quotes"],
    ["estimate", "estimate only", "estimates only"],
    ["contact", "contact form enquiry", "contact form enquiries"],
  ];
  const summary = counted
    .map(([type, one, many]) => [rows.filter((r) => r.type === type).length, one, many] as const)
    .filter(([n]) => n > 0)
    .map(([n, one, many]) => `${fmt(n)} ${n === 1 ? one : many}`)
    .join(" · ");

  return (
    <div className="page">
      <div className="page-head">
        <h1>Estimator Leads</h1>
        <p>
          Every home that got a heat pump estimate (and whether it asked for a callback or got an instant quote), every solar callback, and every contact form
          enquiry. Here we keep addresses only: names, emails, phone numbers and messages go straight to your notification email. An instant quote keeps the
          customer's details with the quote, in Quotes. Records are deleted after a year.
        </p>
      </div>

      {/* Period filter above the table it controls */}
      <div className="dash-filters">
        <div className="ed-segmented" role="group" aria-label="Period">
          {RANGES.map((r) => (
            <button key={r.value} type="button" className={r.value === range ? "is-on" : ""} aria-pressed={r.value === range} onClick={() => setRange(r.value)}>
              {r.label}
            </button>
          ))}
        </div>
      </div>
      {error && <div className="flash flash-danger">{error}</div>}

      <section className="card">
        <div className="leads-head">
          <div>
            <h2>
              {homes ? summary || "No leads yet" : "Loading…"}
            </h2>
            <p className="card-sub">{rangeLabel(range)}. Open a row for everything we hold about it.</p>
          </div>
          <label className="leads-search">
            <Search size={16} aria-hidden="true" />
            <input
              className="input"
              type="search"
              placeholder="Search address, postcode, price, type…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search leads"
            />
          </label>
        </div>

        {!homes && !error && <p className="muted">Loading…</p>}
        {homes && rows.length === 0 && <p className="muted">No leads in this period yet.</p>}
        {rows.length > 0 && shown.length === 0 && <p className="muted">Nothing matches “{search}”.</p>}
        {shown.length > 0 && (
          <div className="table-scroll leads-scroll">
            <table className="stats-table addr-table leads-table">
              <thead>
                <tr>
                  <th scope="col" className="lead-product-col">
                    <span className="sr-only">Product</span>
                  </th>
                  <th scope="col">Type</th>
                  <th scope="col">Address</th>
                  <th scope="col">Details</th>
                  <th scope="col">Component</th>
                  <th scope="col">When</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => {
                  const isOpen = open === r.key;
                  return (
                    <Fragment key={r.key}>
                      <tr className={`lead-row${isOpen ? " is-open" : ""}`} onClick={() => setOpen(isOpen ? null : r.key)}>
                        {/* Which component it came from: heat pump, solar or contact form */}
                        <td className="lead-product-col">
                          <ProductIcon product={r.product} labelled />
                        </td>
                        <td>
                          <span className={`lead-type lead-type-${r.type}`}>
                            {r.type === "callback" ? <PhoneCall size={12} aria-hidden="true" /> : r.type === "quote" ? <FileText size={12} aria-hidden="true" /> : r.type === "contact" ? <Mail size={12} aria-hidden="true" /> : <Calculator size={12} aria-hidden="true" />}
                            {TYPE_LABELS[r.type]}
                          </span>
                        </td>
                        <th scope="row">
                          {/* The button makes the row keyboard-operable; the whole row is clickable */}
                          <button type="button" className="lead-toggle" aria-expanded={isOpen} onClick={(e) => (e.stopPropagation(), setOpen(isOpen ? null : r.key))}>
                            <ChevronRight size={16} aria-hidden="true" className="lead-chevron" />
                            <span>{r.address}</span>
                          </button>
                        </th>
                        <td>{r.quoted}</td>
                        <td className="muted">{names.get((r.record ?? r.enquiry)!.widgetId) ?? "Deleted component"}</td>
                        <td className="muted">{when(r.sortAt)}</td>
                      </tr>
                      {isOpen && (
                        <tr className="lead-detail-row">
                          <td colSpan={6}>
                            {r.record ? (
                              <LeadDetail record={r.record} widgetName={names.get(r.record.widgetId) ?? "Deleted component"} />
                            ) : r.product === "solar" ? (
                              <SolarDetail enquiry={r.enquiry!} widgetName={names.get(r.enquiry!.widgetId) ?? "Deleted component"} />
                            ) : (
                              <EnquiryDetail enquiry={r.enquiry!} widgetName={names.get(r.enquiry!.widgetId) ?? "Deleted component"} />
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {search && shown.length > 0 && (
          <p className="muted leads-count">
            Showing {fmt(shown.length)} of {fmt(rows.length)}
          </p>
        )}
      </section>
    </div>
  );
}

/** A solar callback: the address, the system and price range shown, where it was emailed. */
function SolarDetail({ enquiry, widgetName }: { enquiry: EnquiryRecord; widgetName: string }) {
  const s = enquiry.system;
  return (
    <div className="lead-detail">
      <section>
        <h3>Solar callback</h3>
        <dl className="lead-facts">
          <dt>Component</dt>
          <dd>{widgetName}</dd>
          <dt>Asked</dt>
          <dd>{when(enquiry.createdAt)}</dd>
          <dt>Address</dt>
          <dd>{enquiry.line ?? "Not recorded"}</dd>
          {s ? (
            <>
              <dt>System</dt>
              <dd>
                {s.panels} × {s.panelModel} ({s.kwp.toFixed(2)} kWp) + {s.inverter}
                {s.battery ? ` + ${s.battery}` : ""}
              </dd>
              <dt>Price shown</dt>
              <dd>
                {gbp(s.low)} – {gbp(s.high)}
              </dd>
              <dt>Electricity use</dt>
              <dd>{s.usageKwh.toLocaleString("en-GB")} kWh a year</dd>
            </>
          ) : (
            <>
              <dt>System</dt>
              <dd>None chosen: we couldn't scan this roof, so the homeowner asked for a survey</dd>
            </>
          )}
          <dt>Emailed to</dt>
          <dd>{enquiry.delivery?.to.join(", ") || "Not recorded"}</dd>
        </dl>
        <p className="muted">The homeowner's name and contact details, and the roof figures they saw, went to your inbox in that email. We don't keep them.</p>
      </section>
    </div>
  );
}

/** A contact form enquiry: all we keep (never who sent it: that's in the email). */
function EnquiryDetail({ enquiry, widgetName }: { enquiry: EnquiryRecord; widgetName: string }) {
  return (
    <div className="lead-detail">
      <section>
        <h3>Contact form enquiry</h3>
        <dl className="lead-facts">
          <dt>Form</dt>
          <dd>{widgetName}</dd>
          <dt>Sent</dt>
          <dd>{when(enquiry.createdAt)}</dd>
          <dt>About</dt>
          <dd>{enquiry.topic ?? "Not asked"}</dd>
          <dt>Address</dt>
          <dd>{enquiry.line ?? "Not asked"}</dd>
          <dt>Emailed to</dt>
          <dd>{enquiry.delivery?.to.join(", ") || "Not recorded"}</dd>
        </dl>
        <p className="muted">The sender's name, contact details and message went to your inbox in that email. We don't keep them.</p>
      </section>
      {enquiry.uprn && enquiry.line && (
        <div className="lead-quote">
          <Link
            className="btn btn-primary"
            to={`/quotes/new?${new URLSearchParams({ uprn: enquiry.uprn, line: enquiry.line, widgetId: enquiry.widgetId }).toString()}`}
            onClick={(e) => e.stopPropagation()}
          >
            <FilePlus2 size={16} aria-hidden="true" /> Create a quote for this home
          </Link>
        </div>
      )}
    </div>
  );
}

/** Everything about one home: loaded when its row is opened. */
function LeadDetail({ record, widgetName }: { record: HomeRecord; widgetName: string }) {
  const api = useApi();
  const [detail, setDetail] = useState<HomeDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .getAddress(record.widgetId, record.uprn)
      .then(setDetail)
      .catch((e: Error) => setError(e.message));
  }, [api, record.widgetId, record.uprn]);

  const d = record.details;
  const q = record.quote;
  const epcSummary = detail?.epc?.summary ?? null;
  const epcAnswers = (epcSummary?.details ?? {}) as Record<string, unknown>;

  // The homeowner's answer, marked against what the EPC said
  function answer(field: "propertyType" | "bedrooms" | "heating", text: string) {
    if (!(field in epcAnswers) || !detail) return text;
    const fromEpc = epcAnswers[field];
    if (fromEpc === d?.[field]) return `${text} (from EPC)`;
    const epcText = field === "propertyType" ? PROPERTY[String(fromEpc)] : field === "heating" ? HEATING[String(fromEpc)] : String(fromEpc);
    return `${text} (changed from ${epcText ?? String(fromEpc)} on the EPC)`;
  }

  // The same step-by-step working the homeowner saw (records from before prices
  // were kept on the quote can't be reworked)
  // The RdSAP heat loss recorded with the estimate; older records used the bedrooms rule
  const working =
    d && q
      ? q.heatLossW
        ? quoteWorking(d as PropertyDetails, { ...q, requiredKw: q.heatLossW / 1000 }, "rdsap")
        : quoteWorking(d as PropertyDetails, { ...q, requiredKw: estimateRequiredKw(d as PropertyDetails) }, "legacy")
      : null;

  return (
    <div className="lead-detail">
      {error && <div className="flash flash-danger">{error}</div>}
      <div className="lead-detail-grid">
        <section>
          <h3>Enquiry</h3>
          <dl className="lead-facts">
            <dt>Component</dt>
            <dd>{widgetName}</dd>
            <dt>First seen</dt>
            <dd>{when(record.firstSeen)}</dd>
            <dt>Estimate shown</dt>
            <dd>{when(record.quotedAt)}</dd>
            <dt>Callback requested</dt>
            <dd>{record.callbackRequested ? when(record.callbackAt) : "No"}</dd>
            {record.callbackRequested && (
              <>
                <dt>Sent by</dt>
                <dd>{detail ? (detail.callbackDelivery ? (detail.callbackDelivery.method === "quote" ? `Instant quote ${detail.callbackDelivery.reference}` : "Email") : "Not recorded") : "…"}</dd>
                {detail?.callbackDelivery && detail.callbackDelivery.method === "email" && (
                  <>
                    <dt>Sent to</dt>
                    <dd>{detail.callbackDelivery.to.join(", ")}</dd>
                    <dt>Sent at</dt>
                    <dd>{when(detail.callbackDelivery.sentAt)}</dd>
                  </>
                )}
              </>
            )}
            <dt>UPRN</dt>
            <dd>
              <code>{record.uprn}</code>
            </dd>
          </dl>
        </section>

        <section>
          <h3>Their answers</h3>
          <dl className="lead-facts">
            <dt>Property type</dt>
            <dd>{d ? answer("propertyType", PROPERTY[d.propertyType] ?? d.propertyType) : "—"}</dd>
            <dt>Bedrooms</dt>
            <dd>{d ? answer("bedrooms", d.bedrooms === 5 ? "5+" : String(d.bedrooms)) : "—"}</dd>
            <dt>Current heating</dt>
            <dd>{d ? answer("heating", HEATING[d.heating] ?? d.heating) : "—"}</dd>
            <dt>EPC rating</dt>
            <dd>{detail ? (detail.epc ? (epcSummary?.epcRating ?? "No certificate") : "Not recorded") : "…"}</dd>
            <dt>Floor area</dt>
            <dd>{detail ? (epcSummary?.floorArea ? `${epcSummary.floorArea} m²` : "—") : "…"}</dd>
          </dl>
        </section>
      </div>

      <section>
        <h3>The calculation</h3>
        {working ? (
          <table className="lead-working">
            <tbody>
              {working.map((s) => (
                <tr key={s.label}>
                  <th scope="row">
                    {s.label}
                    <span>{s.sum}</span>
                  </th>
                  <td>{s.result}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="muted">
            {q?.tooBig ? `The home needs more than the largest size you price online (${q.heatPumpKw} kW), so no price was shown. ` : ""}
            {q && !q.tooBig ? `${gbp(q.low)} – ${gbp(q.high)} for a ${q.heatPumpKw} kW heat pump. ` : ""}The full working isn't available for estimates made before
            the calculation was recorded.
          </p>
        )}
      </section>

      {/* Turn the enquiry into a proper quote: the builder starts from this home's EPC */}
      <div className="lead-quote">
        <Link
          className="btn btn-primary"
          to={`/quotes/new?${new URLSearchParams({ uprn: record.uprn, line: record.line ?? "", widgetId: record.widgetId }).toString()}`}
          onClick={(e) => e.stopPropagation()}
        >
          <FilePlus2 size={16} aria-hidden="true" /> Create a quote for this home
        </Link>
      </div>
    </div>
  );
}
